import { useEffect, useRef } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  LocationDetails,
  saveUserPreferences,
} from '@cfa/pathway-fe-shared/xp';

import { useAuth } from '@/hooks/useAuth';
import { useNativeLocationPickerListener } from '@/hooks/useNativeListener';

import { hasNumbersOnlyInString } from '@/utils/string';
import { useRouteMatcher } from '@/utils/url';

import PathwayModal from '@/components/popups/PathwayModal';
import Searchbar from '@/components/Searchbar/Searchbar';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import { LocationCard } from './LocationCard';

import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { licenseeLocationAtom } from '@/atoms/licensee-location';
import { userPrefsAtom } from '@/atoms/userPrefs';

const LicenseeLocationPicker = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routeMatches = useRouteMatcher();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data } = useAuth();
  const userId = data?.userId;
  const userPrefs = useAtomValue(userPrefsAtom);
  const licenseeLocations = data?.licenseeLocations;
  const isLicensee = data?.isLicensee;
  const userIsLicenseeStaff = data?.userIsLicenseeStaff;
  const userSelectedLocation = data?.selectedLocation;

  const [
    { selected: selectedLicenseeLocation, shouldShowPicker },
    sendLicenseeLocationAction,
  ] = useAtom(licenseeLocationAtom);

  const [
    {
      display: { items },
      filters: { search },
    },
    { setSearch, resetSearch, resetAll },
  ] = useDataView<LocationDetails>(DataViewAtomFamilyKey.LICENSEE_LOCATIONS);

  // listening for Native message when location changed in native
  useNativeLocationPickerListener();

  // overriding old default location numbers set to 0, should be able to remove this in a month or two.
  useEffect(() => {
    if (Number(selectedLicenseeLocation?.number) === 0) {
      sendLicenseeLocationAction('setActive', null);
    }
  }, [selectedLicenseeLocation?.number, sendLicenseeLocationAction]);

  // sets initial Licensee Location
  useEffect(() => {
    const matchedLocation = licenseeLocations?.find(
      loc => loc.number === userSelectedLocation,
    );

    const shouldRestoreAuthLocation =
      !!licenseeLocations?.length &&
      !!userSelectedLocation &&
      selectedLicenseeLocation?.number === undefined &&
      !!matchedLocation?.number;

    const shouldInitializeLicenseeLocation =
      isLicensee && !!licenseeLocations?.length && !selectedLicenseeLocation;

    if (shouldRestoreAuthLocation) {
      sendLicenseeLocationAction('setActive', matchedLocation);
    } else if (shouldInitializeLicenseeLocation) {
      if (licenseeLocations.length < 2) {
        sendLicenseeLocationAction('setActive', licenseeLocations[0] ?? null);
      } else if (!window.native.isNative) {
        sendLicenseeLocationAction('show');
      }
    }
  }, [
    isLicensee,
    selectedLicenseeLocation,
    sendLicenseeLocationAction,
    licenseeLocations,
    userSelectedLocation,
  ]);

  const setSource = useSetAtom(sourceDataFamily('licensee-locations'));
  useEffect(() => {
    if (Array.isArray(licenseeLocations)) {
      setSource(licenseeLocations);
    }
  }, [setSource, licenseeLocations]);

  useEffect(() => {
    const storedLicenseeLocation = localStorage.getItem('licenseeLocation');

    if (
      hasNumbersOnlyInString(selectedLicenseeLocation?.number) &&
      (licenseeLocations?.length ?? 0) > 1 &&
      storedLicenseeLocation
    ) {
      toast.custom(toastObj => (
        <ToastMessageBlock
          id={toastObj.id}
          title={t('TrainingPlans.toastMessage.nowViewingLocation1', {
            name: selectedLicenseeLocation?.name,
            number: selectedLicenseeLocation?.number,
          })}
        >
          {t('TrainingPlans.toastMessage.nowViewingLocation2')}
        </ToastMessageBlock>
      ));
      localStorage.removeItem('licenseeLocation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLicenseeLocation]);

  const onClearSearchClick = () => {
    resetSearch();
    searchInputRef.current?.focus();
  };

  const handleClose = () => {
    sendLicenseeLocationAction('hide');
    resetAll();
  };

  const showCloseBtn =
    !!userIsLicenseeStaff ||
    hasNumbersOnlyInString(selectedLicenseeLocation?.number);

  const redirectAfterLocationChange = () => {
    switch (true) {
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingAssignedPath,
      ):
        navigate({ pathname: routes.plans.plans });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingPlanEditPath,
      ):
        navigate({ pathname: routes.plans.managedPlans });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingTeamMembersPath,
      ) ||
        routeMatches.includes(
          Constants.ROUTE_PATH_GROUPS.TrainingTeamMemberProgressPath,
        ):
        navigate({ pathname: routes.plans.managedPlans });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingBuildQuizzesPath,
      ):
        navigate({ pathname: routes.plans.managedQuizzes });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.TrainingEditQuizzesPath,
      ):
        navigate({ pathname: routes.plans.managedQuizzes });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.ReportsPlansViewPath,
      ) ||
        routeMatches.includes(
          Constants.ROUTE_PATH_GROUPS.ReportsTeamMemberProgressViewPath,
        ):
        navigate({ pathname: routes.reports.plans });
        break;
      case routeMatches.includes(
        Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersViewPath,
      ):
        navigate({ pathname: routes.reports.teamMembers });
        break;
    }
  };

  const handleChooseLocation = (location: LocationDetails) => {
    sendLicenseeLocationAction('hide');

    if (selectedLicenseeLocation?.number !== location.number) {
      localStorage.setItem('licenseeLocation', JSON.stringify(location));
      sendLicenseeLocationAction('setActive', location);
      saveUserPreferences({
        body: {
          country: userPrefs.country.id ?? '',
          language: userPrefs.language,
          location: location.number ?? '',
          userId,
        },
      }).catch((err: unknown) => datadogRum.addError(err));
      redirectAfterLocationChange();
    }
  };

  return (
    <PathwayModal
      isOpen={shouldShowPicker}
      onClose={handleClose}
      showCloseButton={showCloseBtn}
      size="lg"
    >
      <PathwayModal.Header>
        <PathwayModal.Title>
          {t('Generic.chooseLicenseeLocation')}
        </PathwayModal.Title>
        <div className="pb-4">
          <Searchbar
            className="z-5 mt-6 mb-2"
            elevation={1}
            onChange={e => {
              setSearch(e.target.value, ['name', 'number']);
            }}
            onClear={onClearSearchClick}
            placeholder={t('Generic.searchLocations')}
            ref={searchInputRef}
            searchValue={search}
            showRecentSearchDropdown
          />
        </div>
      </PathwayModal.Header>
      <PathwayModal.Body>
        {items.map(location => (
          <LocationCard
            key={location.number}
            location={location}
            onChoose={() => handleChooseLocation(location)}
          />
        ))}
      </PathwayModal.Body>
    </PathwayModal>
  );
};

export default LicenseeLocationPicker;
